// @ts-nocheck
import React, { useState, useCallback, useEffect } from 'react';
import { useApolloClient } from 'apollo-3';

import { Box, SvgIcon } from 'Components/Base';
import { getSolution } from 'GraphQL/query/OnlineSolution.query';

import {
  SolutionFooter,
  SearchFilter,
  SolutionContent,
  EmptySolution,
  SolutionLoading,
} from '../../Components/ClearPlus/OnlineSolution';

import { mapSolutionAnswer } from './util';

function OnlineSolution({ sections, sectionList, problemListBySection, currentSectionId }) {
  const client = useApolloClient();
  // TODO: Improvement when open solution by section current
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedProblem, setSelectedProblem] = useState('');
  const [currentProblem, setCurrentProblem] = useState('');
  const [problemList, setProblemList] = useState([]);
  const [solution, setSolution] = useState(null);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const handleReset = () => {
    setSelectedProblem('');
    setCurrentProblem('');
    setSolution(null);
  };

  useEffect(() => {
    const newProblemList = problemListBySection[currentSectionId] ?? [];
    if (newProblemList.length > 0) {
      setSelectedSection(currentSectionId);
      setProblemList(newProblemList);
      handleReset();
    }
  }, [currentSectionId, problemListBySection]);

  const hasSolution = solution && selectedSection && selectedProblem;
  const sectionIndex = sections.findIndex((sec) => sec.sourceId === selectedSection);
  const problemIndex = sections[sectionIndex]?.solutions.findIndex(
    (sol) => sol.id === currentProblem
  );
  const problemNo = problemList[problemIndex]?.label;

  const isLastSection = sectionIndex === sections.length - 1;
  const isLastProblem = problemIndex === sections[sectionIndex]?.solutions.length - 1;
  const isLoading = isPrevLoading || isNextLoading || isSearching;
  const isDisabledBack = (sectionIndex === 0 && problemIndex === 0) || isLoading;
  const isDisabledNext = (isLastSection && isLastProblem) || isLoading;

  const handleSectionChange = useCallback(
    (sectionId) => {
      const newProblemList = problemListBySection[sectionId] ?? [];
      setSelectedSection(sectionId);
      setProblemList(newProblemList);
      handleReset();
    },
    [problemListBySection]
  );

  const handleProblemChange = useCallback((solutionId) => {
    setSelectedProblem(solutionId);
  }, []);

  const fetchSolutionData = useCallback(
    async (solutionId, setLoadingState) => {
      setLoadingState(true);
      try {
        const { data } = await client.query({
          query: getSolution,
          variables: { id: solutionId },
        });
        setSolution(data?.osSolution || null);
      } catch (error) {
        console.error('Error fetching solution:', error);
      } finally {
        setLoadingState(false);
      }
    },
    [client]
  );

  const searchSolution = useCallback(async () => {
    await fetchSolutionData(selectedProblem, setIsSearching);
    setCurrentProblem(selectedProblem);
  }, [fetchSolutionData, selectedProblem]);

  const fetchPrevSolution = useCallback(
    async (solutionId) => {
      await fetchSolutionData(solutionId, setIsPrevLoading);
      setCurrentProblem(solutionId);
    },
    [fetchSolutionData]
  );

  const fetchNextSolution = useCallback(
    async (solutionId) => {
      await fetchSolutionData(solutionId, setIsNextLoading);
      setCurrentProblem(solutionId);
    },
    [fetchSolutionData]
  );

  const goBack = useCallback(async () => {
    if (problemIndex > 0) {
      const prevSolutionId = sections[sectionIndex].solutions[problemIndex - 1].id;
      setSelectedProblem(prevSolutionId);
      await fetchPrevSolution(prevSolutionId);
    } else if (sectionIndex > 0) {
      const prevSection = sections[sectionIndex - 1];
      const prevSolutionId = prevSection.solutions[prevSection.solutions.length - 1].id;
      const newProblemList = problemListBySection[prevSection.sourceId] ?? [];

      setSelectedSection(prevSection.sourceId);
      setSelectedProblem(prevSolutionId);
      setProblemList(newProblemList);
      await fetchPrevSolution(prevSolutionId);
    }
  }, [problemIndex, sectionIndex]);

  const goNext = useCallback(async () => {
    if (problemIndex < sections[sectionIndex].solutions.length - 1) {
      const nextSolutionId = sections[sectionIndex].solutions[problemIndex + 1].id;
      setSelectedProblem(nextSolutionId);
      await fetchNextSolution(nextSolutionId);
    } else if (sectionIndex < sections.length - 1) {
      const nextSection = sections[sectionIndex + 1];
      const nextSolutionId = nextSection.solutions[0].id;
      const newProblemList = problemListBySection[nextSection.sourceId] ?? [];

      setSelectedSection(nextSection.sourceId);
      setSelectedProblem(nextSolutionId);
      setProblemList(newProblemList);
      await fetchNextSolution(nextSolutionId);
    }
  }, [problemIndex, sectionIndex]);

  return (
    <>
      <Box overflow={'auto'} flexGrow={1} padding={6}>
        {!sectionList.length ? (
          <EmptySolution
            title={'คอร์สนี้ยังไม่มี Online Solution'}
            description={'ปัจจุบันคอร์สนี้ยังไม่มีเฉลยแบบ Online Solution'}
            icon={SvgIcon.ICON_LIST.EMPTY_ONLINE_SOLUTION_IN_COURSE_ICON}
          />
        ) : (
          <>
            <SearchFilter
              selectedSection={selectedSection}
              sectionList={sectionList}
              selectedProblem={selectedProblem}
              problemList={selectedSection ? problemList : []}
              onSelectSection={handleSectionChange}
              onSelectProblem={handleProblemChange}
              onSearch={searchSolution}
              isSearching={isLoading}
            />
            {isLoading ? (
              <SolutionLoading />
            ) : hasSolution ? (
              <SolutionContent
                key={solution.id}
                problemNo={problemNo}
                question={solution.question}
                answer={mapSolutionAnswer(solution)}
                solutionDetail={solution.solution}
                type={solution.type}
              />
            ) : (
              <EmptySolution
                title={'เลือกบทเรียน และเลขข้อ'}
                description={'กรุณาเลือกบทเรียนและเลขข้อ เพื่อแสดงข้อมูล Online Solution'}
                icon={SvgIcon.ICON_LIST.EMPTY_ONLINE_SOLUTION_ICON}
              />
            )}
          </>
        )}
      </Box>
      {hasSolution && (
        <SolutionFooter
          onClickNext={goNext}
          onClickBack={goBack}
          disabledBack={isDisabledBack}
          disabledNext={isDisabledNext}
        />
      )}
    </>
  );
}

export default OnlineSolution;
